import { useMemo } from 'react'
import { useFetchDisciplinesQuery } from '../../../../store/apis/discipline.api'
import { useFetchConceptualLabelsForVersionQuery, useFetchConceptualLabelsQuery } from '../../../../store/apis/conceptual-label.api'
import { ConceptualLabelScopes } from '../../../../constants/conceptual-label-scopes'

/**
 * Custom hook that combines discipline data with conceptual labels
 * Ensures all disciplines are shown, even those without labels
 * Note: Always excludes inactive disciplines
 * @param {Object} options - Query options
 * @param {string} options.scope - Discipline scope: ConceptualLabelScopes.ALL, ConceptualLabelScopes.SYSTEM, ConceptualLabelScopes.ORGANIZATION
 * @returns {Object} Combined data and loading states
 */
const useConceptualLabelsByDiscipline = (options = {
    scope: ConceptualLabelScopes.ALL,
    includeEmpty: false,
    token: null
}) => {
    // Fetch all disciplines with the specified scope, always excluding inactive ones
    const { 
        data: disciplines, 
        isFetching: isFetchingDisciplines, 
        error: disciplinesError,
        refetch: refetchDisciplines
    } = useFetchDisciplinesQuery({ 
        scope: options.scope
    })

    const fetchConceptualLabels = options.token ? useFetchConceptualLabelsForVersionQuery : useFetchConceptualLabelsQuery
    
    // Fetch all conceptual labels
    const { 
        data: conceptualLabels, 
        isFetching: isFetchingLabels, 
        error: labelsError,
        refetch: refetchLabels
    } = fetchConceptualLabels({
        token: options.token,
        scope: options.scope
    })

    // Assign child labels under their parent within a discipline and sort by visualId
    const assignChildrenToParents = (labelMap, disciplineId) => {
        // Find all labels for this discipline
        const disciplineLabels = Object.values(labelMap).filter(label => 
            label.disciplineId === disciplineId
        )
        
        // Assign children to parents
        disciplineLabels.forEach(label => {
            if (label.parentId) {
                const parent = disciplineLabels.find(p => p.id === label.parentId)
                if (parent) {
                    parent.children = parent.children || []
                    // Only add if not already added
                    if (!parent.children.some(child => child.id === label.id)) {
                        parent.children.push(label)
                    }
                }
            }
        })
        
        // Sort children by visualId for each parent
        disciplineLabels.forEach(label => {
            if (label.children && label.children.length > 0) {
                label.children = label.children.slice().sort((a, b) => a.visualId - b.visualId)
            }
        })
        
        // Return only root labels (those without parents in this discipline), sorted by visualId
        return disciplineLabels
            .filter(label => !label.parentId)
            .sort((a, b) => a.visualId - b.visualId)
    }

    // Combine the data
    const combinedData = useMemo(() => {
        if (!disciplines || !conceptualLabels) {
            return []
        }

        // Create a map of all conceptual labels by ID
        const labelMap = conceptualLabels.reduce((map, label) => {
            map[label.id] = { ...label }
            return map
        }, {})

        // Build the hierarchy
        const combinedData = disciplines.map(discipline => {
            // Get root labels for this discipline
            const rootLabels = assignChildrenToParents(labelMap, discipline.id)
            
            return {
                id: discipline.id,
                name: discipline.name,
                visualId: discipline.visualId,
                isDiscipline: true,
                children: rootLabels,
                isSystem: discipline.isSystem,
            }
        })

        if (!options.includeEmpty) {
            return combinedData.filter(discipline => discipline.children.length > 0)
        }

        return combinedData
    }, [disciplines, conceptualLabels])

    // Combine refetch functions
    const refetch = () => {
        refetchDisciplines()
        refetchLabels()
    }

    return {
        data: combinedData,
        isFetching: isFetchingDisciplines || isFetchingLabels,
        error: disciplinesError || labelsError,
        refetch
    }
}

export default useConceptualLabelsByDiscipline 